/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { DirectoryCard } from "@cosmos/components";
import { useMessages } from "@epic-mw/i18n";

import type { Instructor } from "@cosmos/airtable-data";
import type { DirectoryCardMetaItem } from "@cosmos/components";

export interface InstructorCardProps {
	data: Instructor;
}

/**
 * Adapter wrapping `DirectoryCard` for a `GoldInstructors` row.
 *
 * Maps `Instructor` fields (country, year qualified, profile links, industry
 * tags) into the generic card props and pulls all chrome text from
 * `@epic-mw/i18n` so translators own the labels.
 */
export function InstructorCard({ data: instructor }: InstructorCardProps): React.JSX.Element {
	const messages = useMessages();

	const links: DirectoryCardMetaItem[] = [];
	if (instructor.linkedInUrl) {
		links.push({
			icon: "LinkedIn",
			label: messages["epic.cosmos.directory.card.linkedIn"] ?? "LinkedIn",
			href: instructor.linkedInUrl,
		});
	}
	if (instructor.artStationUrl) {
		links.push({
			icon: "Globe",
			label: messages["epic.cosmos.directory.card.artStation"] ?? "ArtStation",
			href: instructor.artStationUrl,
		});
	}
	if (instructor.edcUrl) {
		links.push({
			icon: "FileLines",
			label: messages["epic.cosmos.directory.card.instructor.edcProfile"] ?? "EDC profile",
			href: instructor.edcUrl,
		});
	}

	const meta: DirectoryCardMetaItem[] = [];
	if (instructor.country) {
		meta.push({ icon: "LocationDrop", label: instructor.country });
	}

	if (instructor.yearQualified) {
		const sinceTemplate = messages["epic.cosmos.directory.card.instructor.since"] ?? "Since {0}";
		meta.push({ label: sinceTemplate.replace("{0}", instructor.yearQualified) });
	}

	const badgeLabel = messages["epic.cosmos.directory.card.instructor.badge"] ?? "Gold instructor";

	return (
		<DirectoryCard
			name={instructor.name}
			badge={{ label: badgeLabel, tone: "gold" }}
			links={links}
			meta={meta}
			primaryTags={instructor.industries}
		/>
	);
}
